import { useEffect, useState, memo, useCallback, useRef, useMemo } from "react";
import { IGif } from "@/common/services/types";
import { useMediaQuery } from "usehooks-ts";
import { WIDE_TABLET_WIDTH } from "@/common/utils/device";
import { useProgress } from "./hooks/useProgress";
import { usePlaybackControl } from "./hooks/usePlaybackControl";
import { useVisibilityChange } from "./hooks/useVideoEffects";
import { TapTracker } from "components/UI/TapTracker";
import { ProgressBar } from "../ProgressBar";
import { mediaHasAudio } from "./utils/mediaHasAudio";
import { apiMain } from "@/common/services";
import { createHlsUrl } from "@/common/utils/urls";
import "./Player.scss";
import { className } from "@/common/utils";
import { useAppStore, usePortableVideoStore } from "@/common/store";
import { ScrollHint } from "./ScrollHint";
import { useSfw } from "@/common/hooks/useSfw";
import PlayIcon from "@/common/images/icons/play.svg?react";

interface IPlayerProps {
  sound: boolean;
  handleLike: () => void;
  onSound: (sound: boolean, persistent?: boolean, showAnimation?: boolean) => void;
  isVisible: boolean;
  gif: IGif;
  isHLS: boolean;
  position: number;
  poster: string;
  currentUserName: string | undefined;
  sendGifView: (gif: IGif) => void;
  playCount: number;
  incrementPlayCount: () => void;
}

export const Player = memo((props: IPlayerProps) => {
  const {
    sound,
    handleLike,
    onSound,
    isVisible,
    isHLS,
    position,
    poster,
    currentUserName,
    sendGifView,
    playCount,
    incrementPlayCount
  } = props;

  const { sanitizeGif } = useSfw();
  const { gif, replacement } = sanitizeGif(props.gif);

  const startLoadTimeRef = useRef(0);
  const videoWrapRef = useRef(null);

  const [error, setError] = useState<string|null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [forceSd, setForceSd] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const isMobile = useMediaQuery(`(max-width: ${WIDE_TABLET_WIDTH}px)`);
  const videoRef = usePortableVideoStore((state) => state.videoRef);
  const targetRef = usePortableVideoStore((state) => state.targetRef);
  const portVideo = usePortableVideoStore((state) => state.portVideo);
  const updateVideo = usePortableVideoStore((state) => state.updateVideo);
  const isFullScreen = useAppStore((state) => state.isFullScreen);

  // play/pause controls
  const { pause, play, paused, setPaused, isTriedToPlay, startPlayback } = usePlaybackControl({
    gif,
    videoRef,
    onSound,
    sendGifView,
    position,
    playCount,
    incrementPlayCount
  });

  // progress bar controls
  const {
    progress,
    duration,
    handleSeekBarChange,
    handleTimeUpdate,
    handleLoadedMetadata,
    curTimeFormatted
  } = useProgress({ videoRef, startPlayback, incrementPlayCount, paused });

  // tab change actions
  useVisibilityChange({ videoRef, setPaused });

  useEffect(() => {
    setIsActive(targetRef === videoWrapRef);
  }, [videoWrapRef, targetRef]);

  useEffect(() => {
    if (isVisible && !isActive) {
      portVideo(videoWrapRef);
    }
  }, [isVisible]);

  const videoUrl = useMemo(() => {
    // When the video is HLS format
    if (isHLS) {
      const quality = forceSd ? "sd" : "hd";
      return createHlsUrl(replacement?.id ?? gif.id, quality);
    }
    // Non-HLS format videos
    else if (forceSd) {
      return gif.urls.sd;
    }
    else {
      return gif.urls["hd"] || gif.urls.sd;
    }
  }, [gif, isHLS, forceSd]);

  useEffect(() => {
    if (!videoRef.current || !isActive) {
      return;
    }

    if (!isVisible && !videoRef.current.paused) {
      pause();
    }
  }, [isActive, isVisible]);

  useEffect(() => {
    if (isVisible && isLoaded) {
      play();
    }
  }, [isVisible, isLoaded]);

  const handleClick = useCallback(() => {
    if (!videoRef.current || error) {
      return;
    }

    if (!isActive) {
      portVideo(videoWrapRef);
      return;
    }

    videoRef.current.paused ? play() : pause();
  }, [isActive, videoWrapRef, error, play, pause, videoRef]);

  const handleError = useCallback(() => {
    const v = videoRef.current;

    if (v) {
      setError(v.networkState === v.NETWORK_NO_SOURCE ? "Error downloading the video file." : "Unknown error.");
      if (v.networkState === HTMLMediaElement.NETWORK_NO_SOURCE && !forceSd) {
        setForceSd(true);
        setError(null);
      }
    } else {
      console.error("Video playback failed, but the ref is not yet ready.");
    }
  }, [videoRef, forceSd, isHLS]);

  const handleLoadedData = useCallback(() => {
    setIsLoaded(true);

    if (currentUserName === gif.userName && gif.hasAudio !== mediaHasAudio(videoRef)) {
      apiMain.checkGifSound(gif.id);
    }
  }, [videoRef, gif]);

  const handleLoadStart = useCallback(() => {
    setIsLoaded(false);
    startLoadTimeRef.current = performance.now();
  }, []);

  const handleTapAction = useCallback((clicks: number) => {
    if (clicks === 1) {
      handleClick();
    }

    if (clicks === 2) {
      handleLike();
    }
  }, [handleClick, handleLike]);

  const handleHoldStart = useCallback(() => {
    pause();
  }, [pause]);

  const handleHoldEnd = useCallback(() => {
    play();
  }, [play]);

  const handleContextMenu = useCallback((
    event: React.MouseEvent<HTMLVideoElement, MouseEvent> |
    React.MouseEvent<SVGSVGElement, MouseEvent> |
    React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
  }, []);

  const contextMenuHandler = isMobile ? handleContextMenu : undefined;

  const videoAttributes = useMemo(() => {
    return {
      className: className({
        isError: !!error,
        isLoaded: isLoaded,
      }),
      controls: false,
      autoPlay: false,
      loop: true,
      playsInline: true,
      preload: "metadata",
      onError: handleError,
      onLoadStart: handleLoadStart,
      onLoadedData: handleLoadedData,
      onLoadedMetadata: handleLoadedMetadata,
      onTimeUpdate: handleTimeUpdate,
      muted: !sound,
      disablePictureInPicture: true,
      src: videoUrl,
      onContextMenu: contextMenuHandler,
    };
  }, [
    sound,
    isLoaded,
    error,
    handleError,
    handleLoadStart,
    handleLoadedData,
    handleLoadedMetadata,
    handleTimeUpdate,
    videoUrl,
    contextMenuHandler
  ]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    updateVideo(videoAttributes);
  }, [isActive, videoAttributes, isFullScreen]);

  return (
    <>
      <TapTracker onTap={handleTapAction} onHold={handleHoldStart} onRelease={handleHoldEnd}>
        <div className="Player">
          <img className="Player-Poster" src={poster} alt={`Poster for ${gif.id}`}/>
          <div className="Player-Video" ref={videoWrapRef}></div>
          <div className="Player-OverLayer"></div>

          {paused && isTriedToPlay && !error && (
            <PlayIcon
              className="playButton"
              onContextMenu={contextMenuHandler}
            />
          )}

          {error && <p className="Player-ErrorMessage">{error}</p>}
        </div>
      </TapTracker>
      <div className="Player-ProgressBar">
        <ProgressBar
          max={duration}
          currentTime={progress}
          onChange={handleSeekBarChange}
        />
        <span className="countdown">{curTimeFormatted}</span>
      </div>
      {isActive && (
        <ScrollHint position={position} progress={progress} />
      )}
    </>
  );
});
